import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Link, useNavigate } from "react-router-dom";
import Pagination from "../Pagination/Pagination";
import { Card, CardContent } from "@mui/material";
import { FaHome, FaStar } from "react-icons/fa";
import { GoClock } from "react-icons/go";
import { MdOutlineEmail } from "react-icons/md";
import { HiUser } from "react-icons/hi2";

const getAgencyReviewStats = (reviews) => {
  const list = Array.isArray(reviews) ? reviews : [];
  if (list.length === 0) {
    return { average: 0, totalCount: 0, last12MonthsCount: 0 };
  }

  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

  let starSum = 0;
  let ratedCount = 0;
  let last12MonthsCount = 0;

  for (const review of list) {
    const star = Number(review?.star ?? review?.rating);
    if (Number.isFinite(star) && star > 0) {
      ratedCount += 1;
      starSum += Math.min(5, star);
    }

    const createdAt = review?.createdAt ?? review?.date;
    if (createdAt && !Number.isNaN(new Date(createdAt).getTime())) {
      if (new Date(createdAt) >= twelveMonthsAgo) {
        last12MonthsCount += 1;
      }
    }
  }

  const average =
    ratedCount > 0 ? Math.round((starSum / ratedCount) * 10) / 10 : 0;

  return {
    average,
    totalCount: list.length,
    last12MonthsCount,
  };
};

const formatAgencyAddress = (agency) =>
  [agency?.street, agency?.suburb_area, agency?.postcode]
    .filter(Boolean)
    .join(", ");

const getAgencyPerformance = (agency) => {
  const sales = agency?.salesPerformance ?? {};
  const rent = agency?.rentPerformance ?? {};

  return {
    sales: {
      total: sales.totalPropertiesSold ?? 0,
      medianDays: sales.medianDaysAdvertised ?? 0,
      region: sales.region?.trim() || "—",
    },
    rent: {
      total: rent.totalPropertiesLeased ?? 0,
      medianDays: rent.medianDaysAdvertised ?? 0,
      region: rent.region?.trim() || "—",
    },
  };
};

const AgencyListEmptyState = ({ browseTo }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <HiUser className="text-[#097FE6]" size={36} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No agencies to show
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      There aren&apos;t any agencies listed for this area yet. Try the main find-agents page or check back later.
    </p>
    <Link to={browseTo} className="w-full sm:w-auto">
      <button
        type="button"
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Find agencies
      </button>
    </Link>
  </div>
);

const AgencyListSearchEmptyState = ({ onClearSearch }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <HiUser className="text-[#097FE6]" size={36} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No matching agencies
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Nothing matches that search. Clear the search box or try different keywords.
    </p>
    {typeof onClearSearch === "function" ? (
      <button
        type="button"
        onClick={onClearSearch}
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Clear search
      </button>
    ) : null}
  </div>
);

const AgencyListErrorState = ({ onRetry, browseTo }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#FEF2F2] flex items-center justify-center mb-5 ring-1 ring-[#FECACA]"
      aria-hidden
    >
      <span className="text-2xl sm:text-3xl font-semibold text-[#DC2626]" aria-hidden>
        !
      </span>
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      Couldn&apos;t load agencies
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Something went wrong while fetching the list. Check your connection and try again.
    </p>
    {typeof onRetry === "function" ? (
      <button
        type="button"
        onClick={onRetry}
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Try again
      </button>
    ) : (
      <Link to={browseTo} className="w-full sm:w-auto">
        <button
          type="button"
          className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
        >
          Find agencies
        </button>
      </Link>
    )}
  </div>
);

const FindAgencyCard = (props) => {
  const navigate = useNavigate();
  const {
    AgenciesData,
    fetchError = false,
    onRetry,
    agencySourceCount,
    onClearSearch,
    browseTo = "/find-agents",
  } = props;

  const list = Array.isArray(AgenciesData) ? AgenciesData : [];
  const sourceTotal = typeof agencySourceCount === "number" ? agencySourceCount : list.length;

  let PageSize = 5;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [AgenciesData]);
  // Assuming you have an array of data called `myData`
  const startIndex = (currentPage - 1) * PageSize;
  const endIndex = startIndex + PageSize;
  const currentData = list.slice(startIndex, endIndex);

  return (
    <>
      <div className="w-full max-w-full min-w-0 pb-8">
        {fetchError ? (
          <AgencyListErrorState onRetry={onRetry} browseTo={browseTo} />
        ) : list.length === 0 && sourceTotal > 0 ? (
          <AgencyListSearchEmptyState onClearSearch={onClearSearch} />
        ) : list.length === 0 ? (
          <AgencyListEmptyState browseTo={browseTo} />
        ) : (
          currentData?.map((i, index) => {
            const reviewStats = getAgencyReviewStats(i?.agencyReviews);
            const performance = getAgencyPerformance(i);
            const addressLine = formatAgencyAddress(i);

            return (
              <div key={i?.id ?? index} className="w-full bg-gray-50 pb-8">

                {/* Main content */}
                <div className="" onClick={() => navigate(`/agency-profile/${i?.id}`)}>
                  <Card className="shadow-md overflow-hidden rounded-xl sm:rounded-2xl ">
                    <CardContent className="p-0">
                      <div className="mb-3">
                        {/* Header with yellow banner */}
                        <div
                          className={`w-full flex items-center px-3 py-2 ${i?.primary_color ? "" : "bg-[#097FE6]"}`}
                          style={i?.primary_color ? { backgroundColor: i.primary_color } : { backgroundColor: "#097FE6" }}
                        >
                          <LazyLoadImage
                            src={i?.agencyLargeLogo}
                            alt="icon"
                            loading="lazy"
                            effect="blur"
                            className="h-7 object-contain max-w-[140px] sm:max-w-[180px]"
                          />
                        </div>
                        <div className="flex flex-col lg:flex-row lg:flex-nowrap w-full p-4 md:p-5 lg:gap-0">
                          <div className="w-full lg:w-[40%] lg:min-w-0 lg:shrink-0 lg:pr-5 pb-5 lg:pb-0 border-b lg:border-b-0 border-[#E5E3E8]">
                            <h2 className="text-lg md:text-2xl font-semibold text-gray-800">
                              {i?.name}
                            </h2>
                            {addressLine ? (
                              <p className="text-gray-600 mt-1 text-sm leading-relaxed">
                                {addressLine}
                              </p>
                            ) : null}

                            {reviewStats.totalCount > 0 ? (
                              <>
                                <div className="flex items-center gap-2 mt-2">
                                  <div className="flex justify-center items-center gap-1">
                                    <FaStar size={16} className="text-[#FFB200]" />
                                    <span className="font-medium">
                                      {reviewStats.average.toFixed(1)}
                                    </span>
                                  </div>
                                  <span className="text-gray-600">
                                    ({reviewStats.totalCount}{" "}
                                    {reviewStats.totalCount === 1
                                      ? "review"
                                      : "reviews"})
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                  {reviewStats.last12MonthsCount}{" "}
                                  {reviewStats.last12MonthsCount === 1
                                    ? "review"
                                    : "reviews"}{" "}
                                  in last 12 months
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-500 mt-2">
                                No reviews yet
                              </p>
                            )}
                          </div>

                          <div className="w-full lg:w-[30%] lg:min-w-0 lg:shrink-0 lg:px-5 py-5 lg:py-0 ">
                            <h3 className="text-base font-medium text-gray-800 mb-4">
                              Sales performance
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="min-w-0">
                                <FaHome className="text-gray-700 mb-2" />
                                <p className="text-xl font-bold text-gray-900">
                                  {performance.sales.total}
                                </p>
                                <p className="text-xs text-gray-600 mt-2 leading-snug">
                                  <span className="block">Properties sold in</span>
                                  <span className="block break-words">
                                    {performance.sales.region}
                                  </span>
                                </p>
                              </div>
                              <div className="min-w-0">
                                <GoClock className="text-gray-700 mb-2" />
                                <p className="text-xl font-bold text-gray-900">
                                  {performance.sales.medianDays}
                                </p>
                                <p className="text-xs text-gray-600 mt-2 leading-snug">
                                  Median days advertised
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="w-full lg:w-[30%] lg:min-w-0 lg:shrink-0 lg:pl-5 pt-5 lg:pt-0 lg:border-l border-[#E5E3E8]">
                            <h3 className="text-base font-medium text-gray-800 mb-4">
                              Rent performance
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="min-w-0">
                                <FaHome className="text-gray-700 mb-2" />
                                <p className="text-xl font-bold text-gray-900">
                                  {performance.rent.total}
                                </p>
                                <p className="text-xs text-gray-600 mt-2 leading-snug">
                                  <span className="block">Properties leased in</span>
                                  <span className="block break-words">
                                    {performance.rent.region}
                                  </span>
                                </p>
                              </div>
                              <div className="min-w-0">
                                <GoClock className="text-gray-700 mb-2" />
                                <p className="text-xl font-bold text-gray-900">
                                  {performance.rent.medianDays}
                                </p>
                                <p className="text-xs text-gray-600 mt-2 leading-snug">
                                  Median days advertised
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-[#E5E3E8] px-4 md:px-5 py-4">
                        <div className="pt-4 flex  justify-end items-center">
                          <div className="flex flex-col md:flex-row justify-end items-center pr-4 gap-3 w-full lg:w-[60%] px-6">
                            <button
                              className="w-full flex justify-center items-center gap-2 text-base font-normal rounded-full border border-[#959199] text-[#3D3B40] py-3 px-2 cursor-pointer text- text-center"
                              onClick={() => { }}
                            >
                              <MdOutlineEmail className="w-4 cursor-pointer" />
                              <span>Enquire</span>
                            </button>
                            <button
                              className="w-full flex justify-center items-center gap-2 text-base font-normal rounded-full bg-[#097FE6] py-3 px-2 cursor-pointer text-white text-center"
                              onClick={() => {
                                // navigate(`/appraisal/${i?._id}`);
                              }}
                            >
                              <MdOutlineEmail className="w-4 cursor-pointer" />
                              <span>Request a free appraisal</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* <<----- Pagination ----->> */}
      {!fetchError && list.length > 0 && (
        <div className="flex justify-center items-center pb-4">
          <Pagination
            className="pagination-bar"
            currentPage={currentPage}
            totalCount={list.length}
            pageSize={PageSize}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}
    </>
  );
};

export default FindAgencyCard;
